import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Language } from '@shared/types';
import { useBrandingContext } from '../contexts/BrandingContext';
import { getErrorMessage } from '@shared/utils/errorUtils';
import { LoadingButton, InlineError } from '../components/ui';
import { appCheckedFetch } from '../utils/appCheckedFetch';

interface ParentOption {
  name: string;
  email: string;
}

interface TokenInfo {
  valid: boolean;
  expired?: boolean;
  used?: boolean;
  tokenType?: 'parent' | 'teacher';
  schoolName?: string;
  memberNo?: string;
  parents?: ParentOption[];
  teacherName?: string;
  teacherEmail?: string;
}

export default function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logoUrl, primaryColor } = useBrandingContext();
  const token = searchParams.get('token');

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [validating, setValidating] = useState(true);
  const [selectedParentIndex, setSelectedParentIndex] = useState(0);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    preferredLanguage: i18n.language as Language,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Validate token on load
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    async function validateToken() {
      try {
        const response = await appCheckedFetch(
          `https://europe-west3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/validateInvitationToken`,
          {
            method: 'POST',
            body: JSON.stringify({ token }),
          }
        );

        if (!response.ok) {
          console.error('Token validation failed:', response.status);
          setTokenInfo({ valid: false });
          return;
        }

        const data = await response.json();
        console.log('Token validation response:', data);

        // PR #3b: parent tokens are now handled by /enroll-complete (unified
        // enrollment-style flow with prefill + extended fields). Teacher
        // tokens stay on /signup which is a minimal password-only flow.
        // This redirect makes the new behavior available even for legacy
        // emails that still link to /signup.
        if (data.valid && data.tokenType === 'parent') {
          navigate(`/enroll-complete?token=${encodeURIComponent(token!)}`, { replace: true });
          return;
        }

        setTokenInfo(data);
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenInfo({ valid: false });
      } finally {
        setValidating(false);
      }
    }

    validateToken();
  }, [token, navigate]);

  const selectedParent = tokenInfo?.parents?.[selectedParentIndex];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const isTeacher = tokenInfo?.tokenType === 'teacher';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t('signup.errors.invalidToken'));
      return;
    }

    if (!isTeacher && !selectedParent) {
      setError(t('signup.errors.invalidToken'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('signup.errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('signup.errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isTeacher) {
        // Teacher onboarding via callable function
        const functions = getFunctions(undefined, 'europe-west3');
        const onboardTeacher = httpsCallable(functions, 'onboardTeacher');
        const result = await onboardTeacher({
          token,
          email: tokenInfo!.teacherEmail,
          password: formData.password,
          name: tokenInfo!.teacherName,
          preferredLanguage: formData.preferredLanguage,
        });

        const data = result.data as { success?: boolean; message?: string };
        if (data.success) {
          setSuccess(true);
        } else {
          setError(data.message || t('signup.errors.createFailed'));
        }
      } else {
        // Parent onboarding via HTTP
        const response = await appCheckedFetch(
          `https://europe-west3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/onboardParentHttp`,
          {
            method: 'POST',
            body: JSON.stringify({
              token,
              email: selectedParent!.email,
              password: formData.password,
              name: selectedParent!.name,
              preferredLanguage: formData.preferredLanguage,
            }),
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          setSuccess(true);
        } else {
          setError(data.message || t('signup.errors.createFailed'));
        }
      }
    } catch (err: unknown) {
      console.error('Error creating account:', err);
      const message = getErrorMessage(err) || t('signup.errors.createFailed');
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  // DEBUG: Log render state
  console.log('SignupPage render:', { validating, success, token: !!token, tokenInfo, error });

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{String(t('signup.validating'))}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{String(t('signup.successTitle'))}</h2>
          <p className="text-gray-600 mb-6">{String(t('signup.successMessage'))}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary w-full"
          >
            {String(t('signup.goToLogin'))}
          </button>
        </div>
      </div>
    );
  }

  // Invalid/expired/used token
  if (!token || !tokenInfo?.valid) {
    const message = tokenInfo?.used
      ? String(t('signup.tokenUsed'))
      : tokenInfo?.expired
      ? String(t('signup.tokenExpired'))
      : String(t('signup.tokenInvalid'));

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{String(t('signup.tokenErrorTitle'))}</h2>
          <p className="text-gray-600 mb-6">{message}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary"
          >
            {String(t('signup.goToLogin'))}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={tokenInfo.schoolName || ''}
                className="h-16 w-auto mx-auto mb-3 object-contain"
              />
            )}
            <h1
              className="text-xl sm:text-2xl font-bold leading-tight"
              style={{ color: primaryColor }}
            >
              {String(t('signup.title'))}
            </h1>
            {tokenInfo.schoolName && (
              <p className="mt-2 text-sm text-gray-600">
                {String(tokenInfo.schoolName)}
              </p>
            )}
            {tokenInfo.memberNo && (
              <p className="mt-1 text-xs text-gray-500">
                {String(t('signup.memberNo'))}: {String(tokenInfo.memberNo)}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Teacher info */}
            {isTeacher ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">{String(t('signup.registeredAs'))}</div>
                <div className="font-medium text-gray-900">{tokenInfo.teacherName ? String(tokenInfo.teacherName) : ''}</div>
                <div className="text-sm text-gray-600">{tokenInfo.teacherEmail ? String(tokenInfo.teacherEmail) : ''}</div>
              </div>
            ) : tokenInfo.parents && tokenInfo.parents.length > 1 ? (
              /* Parent Selector (if 2 parents) */
              <div>
                <label className="label">{String(t('signup.selectParent'))}</label>
                <div className="space-y-2">
                  {tokenInfo.parents.map((parent, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedParentIndex === idx
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="parentSelect"
                        checked={selectedParentIndex === idx}
                        onChange={() => setSelectedParentIndex(idx)}
                        className="text-primary-600"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{String(parent.name)}</div>
                        <div className="text-sm text-gray-500">{String(parent.email)}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              /* Single parent - show info */
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">{String(t('signup.registeredAs'))}</div>
                <div className="font-medium text-gray-900">{selectedParent?.name ? String(selectedParent.name) : ''}</div>
                <div className="text-sm text-gray-600">{selectedParent?.email ? String(selectedParent.email) : ''}</div>
              </div>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="label">
                {String(t('signup.password'))}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="input"
                placeholder={String(t('signup.passwordPlaceholder'))}
              />
              <p className="mt-1 text-xs text-gray-500">
                {String(t('signup.passwordHint'))}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="label">
                {String(t('signup.confirmPassword'))}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input"
                placeholder={String(t('signup.confirmPasswordPlaceholder'))}
              />
            </div>

            {/* Preferred Language */}
            <div>
              <label htmlFor="preferredLanguage" className="label">
                {String(t('signup.preferredLanguage'))}
              </label>
              <select
                id="preferredLanguage"
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
                className="input"
              >
                <option value="bg">Български</option>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>

            <InlineError
              message={error ? String(error) : ''}
              onDismiss={() => setError(null)}
              scrollIntoView
            />

            {/* Submit Button */}
            <LoadingButton
              type="submit"
              loading={loading}
              loadingText={String(t('signup.creating'))}
              className="w-full btn btn-primary"
            >
              {String(t('signup.create'))}
            </LoadingButton>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {String(t('signup.haveAccount'))}{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {String(t('signup.login'))}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
